import React, { useState, useEffect } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Share2, Wallet, ArrowDownToLine, Users2, ShieldCheck,
  ShoppingBag, Smartphone, Receipt, LifeBuoy, UserCog, LogOut, Menu, X, Bell, FileSearch, Globe, Plus, Info,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LANGS } from "@/context/LanguageContext";
import api, { inr } from "@/lib/api";
import Overview from "@/pages/dashboard/Overview";
import Refer from "@/pages/dashboard/Refer";
import LeadStatus from "@/pages/dashboard/LeadStatus";
import WalletPage from "@/pages/dashboard/Wallet";
import Withdraw from "@/pages/dashboard/Withdraw";
import KYC from "@/pages/dashboard/KYC";
import Network from "@/pages/dashboard/Network";
import Profile from "@/pages/dashboard/Profile";
import Support from "@/pages/dashboard/Support";
import Services from "@/pages/dashboard/Services";

const NAV = [
  ["", "dash_home", LayoutDashboard],
  ["refer", "dash_refer", Share2],
  ["lead-status", "dash_leadstatus", FileSearch],
  ["wallet", "dash_wallet", Wallet],
  ["withdraw", "dash_withdraw", ArrowDownToLine],
  ["network", "dash_network", Users2],
  ["kyc", "dash_kyc", ShieldCheck],
  ["shopping", "dash_shopping", ShoppingBag],
  ["recharge", "dash_recharge", Smartphone],
  ["billpay", "dash_billpay", Receipt],
  ["support", "dash_support", LifeBuoy],
  ["profile", "dash_profile", UserCog],
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showN, setShowN] = useState(false);
  const [kycBannerDismissed, setKycBannerDismissed] = useState(() => sessionStorage.getItem("ck_kyc_banner_off") === "1");

  useEffect(() => {
    api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const SidebarInner = () => (
    <>
      <div className="p-5"><Logo height={36} /></div>
      <nav className="px-3 space-y-1 flex-1 overflow-y-auto no-scrollbar pb-4">
        {NAV.filter(([path]) => path !== "withdraw" || user?.kyc_status === "verified").map(([path, labelKey, Icon]) => (
          <NavLink key={path} to={`/dashboard/${path}`} end={path === ""} onClick={() => setOpen(false)}
            data-testid={`dash-nav-${path || "home"}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-[#0B223D] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> {t(labelKey)}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-200">
        <button onClick={async () => { await logout(); nav("/"); }} data-testid="dash-logout"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-600 hover:bg-rose-50 w-full">
          <LogOut className="w-4 h-4" /> {t("dash_signout")}
        </button>
        <div className="flex items-center gap-2 mt-2 px-1">
          <div className="relative flex items-center flex-1">
            <Globe className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 pointer-events-none" />
            <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="dash-language-switcher" aria-label="Language"
              className="w-full h-9 pl-8 pr-2 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-600 appearance-none cursor-pointer">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
          <ThemeToggle />
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-slate-200 h-screen sticky top-0">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-white flex flex-col h-full"><SidebarInner /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 glass bg-white/85 border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="dash-mobile-menu"><Menu /></button>
          <div className="hidden sm:block">
            <span className="text-sm text-slate-400">User ID</span>
            <span className="ml-2 font-mono-num font-bold text-[#0B223D]">{user?.user_code}</span>
          </div>
          <div className="flex items-center gap-3 relative">
            <button onClick={() => { setShowN(!showN); if (!showN) { api.post("/notifications/read").then(() => setNotifs(notifs.map(n => ({ ...n, read: true })))); } }}
              className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="dash-notif-btn">
              <Bell className="w-5 h-5 text-slate-600" />
              {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 text-white text-[10px] rounded-full flex items-center justify-center">{unread}</span>}
            </button>
            {showN && (
              <div className="absolute right-0 top-12 w-80 bg-white rounded-xl border border-slate-200 shadow-xl max-h-96 overflow-y-auto z-50" data-testid="dash-notif-panel">
                {notifs.length === 0 ? <p className="p-4 text-sm text-slate-400">No notifications yet.</p> :
                  notifs.map((n) => (
                    <div key={n.id} className="p-3.5 border-b border-slate-100 last:border-0">
                      <p className="font-semibold text-sm text-[#0B223D]">{n.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>
                    </div>
                  ))}
              </div>
            )}
            <div className="w-9 h-9 rounded-full bg-[#0B223D] text-white font-bold flex items-center justify-center">{user?.name?.[0]?.toUpperCase()}</div>
          </div>
        </header>

        {user && user.kyc_status !== "verified" && !kycBannerDismissed && (
          <div data-testid="kyc-withdrawal-banner" className="bg-amber-400 text-[#0B223D] px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 text-sm font-semibold">
            <span className="flex items-center gap-2 min-w-0">
              <Info className="w-4 h-4 shrink-0" />
              <span className="truncate">Wallet Withdrawal sirf <b>KYC verification complete</b> hone ke baad enable hoga.</span>
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => nav("/dashboard/kyc")} data-testid="kyc-banner-cta" className="bg-[#0B223D] text-white px-3.5 py-1.5 rounded-lg text-xs font-bold hover:bg-[#06182B] transition-colors whitespace-nowrap">Complete KYC</button>
              <button onClick={() => { setKycBannerDismissed(true); sessionStorage.setItem("ck_kyc_banner_off", "1"); }} data-testid="kyc-banner-close" aria-label="Dismiss" className="p-1 rounded hover:bg-black/10"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        <main className="p-4 sm:p-8 pb-24 lg:pb-8">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="refer" element={<Refer />} />
            <Route path="lead-status" element={<LeadStatus />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="withdraw" element={<Withdraw />} />
            <Route path="network" element={<Network />} />
            <Route path="kyc" element={<KYC />} />
            <Route path="shopping" element={<Services service="shopping" />} />
            <Route path="recharge" element={<Services service="recharge" />} />
            <Route path="billpay" element={<Services service="billpay" />} />
            <Route path="support" element={<Support />} />
            <Route path="profile" element={<Profile />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
